import React, { useState, useEffect } from 'react';
import { Linkedin, Github, MessageCircle } from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';


const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.devsithum.com';

// reCAPTCHA configuration
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LfK9josAAAAAE7nxIbhlfDKCb-7xLg5DclI8F-I';


const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [submissionTimes, setSubmissionTimes] = useState([]);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [recaptchaLoading, setRecaptchaLoading] = useState(false);
  // const recaptchaRef = useRef(null);

  // // API URL configuration - this is the key fix
  // const API_BASE_URL = process.env.REACT_APP_API_URL || 
  //                     (process.env.NODE_ENV === 'production' ? '' : 'https://api.devsithum.com');

  // Load reCAPTCHA script
  useEffect(() => {
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      window.grecaptcha.ready(() => {
        console.log('reCAPTCHA loaded successfully');
        setRecaptchaLoaded(true);
      });
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA script');
      setErrors(prev => ({ ...prev, recaptcha: 'Failed to load reCAPTCHA. Please refresh the page.' }));
    };
    
    document.head.appendChild(script);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Rate limiting check (using state instead of localStorage)
  const checkRateLimit = () => {
    const now = Date.now();
    const oneMinute = 60 * 1000;
    
    // Filter submissions from the last minute
    const recentSubmissions = submissionTimes.filter(time => now - time < oneMinute);
    
    if (recentSubmissions.length >= 2) {
      return false;
    }
    
    // Add current submission
    setSubmissionTimes(prev => [...recentSubmissions, now]);
    return true;
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = 'Name can only contain letters and spaces';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.trim().length > 1000) {
      newErrors.message = 'Message must be less than 1000 characters';
    }
    
    // reCAPTCHA validation
    if (!recaptchaToken) {
      newErrors.recaptcha = 'Please complete the reCAPTCHA verification';
    }
    
    return newErrors;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle reCAPTCHA
  const handleRecaptcha = async () => {
    if (!recaptchaLoaded) {
      setErrors(prev => ({ ...prev, recaptcha: 'reCAPTCHA is still loading. Please wait a moment and try again.' }));
      return;
    }

    if (!window.grecaptcha) {
      setErrors(prev => ({ ...prev, recaptcha: 'reCAPTCHA not loaded. Please refresh the page.' }));
      return;
    }

    setRecaptchaLoading(true);
    setErrors(prev => ({ ...prev, recaptcha: '' }));

    try {
      const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'contact' });
      
      if (token) {
        setRecaptchaToken(token);
        console.log('reCAPTCHA token generated successfully');
      } else {
        throw new Error('No token received');
      }
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      setErrors(prev => ({ 
        ...prev, 
        recaptcha: 'reCAPTCHA verification failed. Please try again or refresh the page.' 
      }));
    } finally {
      setRecaptchaLoading(false);
    }
  };

  // Handle form submission - IMPROVED ERROR HANDLING
  const handleSubmit = async () => {
    
    // Check rate limiting
    if (!checkRateLimit()) {
      setSubmitStatus({
        type: 'error',
        message: 'Too many submissions. Please wait a minute before trying again.'
      });
      return;
    }
    
    // Validate form
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    setSubmitStatus(null);
    
    try {
      console.log('Sending request to:', `${API_BASE_URL}/api/contact`);
      
      // Backend API call with improved error handling
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          recaptchaToken
        }),
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      // Check if response is ok
      if (!response.ok) {
        let errorMessage = 'Failed to send message';
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('Server error response:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          errorMessage = `Server error (${response.status}): ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }
      
      // Parse successful response
      const result = await response.json();
      console.log('Success response:', result);
      
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
      });
      setFormData({ name: '', email: '', message: '' });
      setRecaptchaToken(null);
      
    } catch (error) {
      console.error('Submit error details:', error);
      
      // More specific error handling
      let errorMessage = 'Sorry, there was an error sending your message. Please try again later.';
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Unable to connect to server. Please check your connection and try again.';
      } else if (error.message.includes('CORS')) {
        errorMessage = 'Connection blocked. Please contact support.';
      } else if (error.message && error.message !== 'Failed to send message') {
        errorMessage = error.message;
      }
      
      setSubmitStatus({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-transparent py-12 px-4 sm:px-6 lg:px-8">
      <div className="relative max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="rounded-2xl p-8 shadow-lg shadow-blue-500/40 bg-slate-950">
              <h2 className="text-2xl font-semibold text-white mb-6">Get in Touch</h2>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white text-left font-medium">Email</h3>
                    <p className="text-gray-400">contact@devsithum.com</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-white text-left font-medium">Location</h3>
                    <p className="text-gray-400">Panadura, Sri Lanka</p>
                  </div>
                </div>

                {/* Social Links */}
                <div className="flex space-x-4">
                  {[
                    { name: 'LinkedIn', icon: <Linkedin className="w-5 h-5" />, href: 'https://www.linkedin.com/in/sithum-gimhana-a47a27294' },
                    { name: 'GitHub', icon: <Github className="w-5 h-5" />, href: 'https://github.com/SithumGimhana' },
                    { name: 'WhatsApp', icon: <MessageCircle className="w-5 h-5" />, href: 'https://wa.me/758533225' },
                    { name: 'TikTok', icon: <FaTiktok className="w-5 h-5" />, href: 'https://www.tiktok.com/@devsithum' }
                  ].map((social) => (
                    <a
                      key={social.name}
                      href={social.href}
                      target='_blank'
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-white/10 backdrop-blur-lg rounded-lg flex items-center justify-center text-xl hover:bg-white/20 transition-all duration-300 hover:scale-110 border border-white/10"
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </div>

              </div>
            </div>



          {/* Contact Form */}
          <div className="bg-slate-950 backdrop-blur-lg rounded-2xl p-8 shadow-lg shadow-blue-500/40">
            <div className="space-y-6">
              {/* Status Messages */}
              {submitStatus && (
                <div className={`p-4 rounded-lg border ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-500/10 border-green-500/20 text-green-400' 
                    : 'bg-red-500/10 border-red-500/20 text-red-400'
                }`}>
                  <div className="flex items-center space-x-2">
                    {submitStatus.type === 'success' ? (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    )}
                    <span className="text-sm font-medium">{submitStatus.message}</span>
                  </div>
                </div>
              )}

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-left text-sm font-medium text-gray-300 mb-2">
                  Name 
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white/10 border ${
                    errors.name ? 'border-red-500' : 'border-white/20'
                  } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-200`}
                  placeholder="Your Name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-left text-red-400">{errors.name}</p>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-left text-sm font-medium text-gray-300 mb-2">
                  Email 
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white/10 border ${
                    errors.email ? 'border-red-500' : 'border-white/20'
                  } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-200`}
                  placeholder="your.email@example.com"
                />
                {errors.email && (
                  <p className="mt-1 text-left text-sm text-red-400">{errors.email}</p>
                )}
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-left text-sm font-medium text-gray-300 mb-2">
                  Your Message 
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 bg-white/10 border ${
                    errors.message ? 'border-red-500' : 'border-white/20'
                  } rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all duration-200 resize-none`}
                  placeholder="Tell me about your project, ideas, or just say hello..."
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.message ? (
                    <p className="text-sm text-red-400">{errors.message}</p>
                  ) : (
                    <span className="text-sm text-gray-500">
                      {formData.message.length}/1000 characters
                    </span>
                  )}
                </div>
              </div>

              {/* reCAPTCHA */}
              <div>
                <button
                  type="button"
                  onClick={handleRecaptcha}
                  disabled={recaptchaLoading || !recaptchaLoaded}
                  className={`w-full px-4 py-3 border ${
                    errors.recaptcha ? 'border-red-500' : recaptchaToken ? 'border-green-500' : 'border-white/20'
                  } rounded-lg text-white transition-all duration-200 flex items-center justify-center space-x-2 ${
                    recaptchaLoading || !recaptchaLoaded 
                      ? 'bg-gray-600 cursor-not-allowed' 
                      : recaptchaToken 
                        ? 'bg-green-600/20 hover:bg-green-600/30' 
                        : 'bg-white/10 hover:bg-white/20'
                  }`}
                >
                  {recaptchaLoading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Verifying...</span>
                    </>
                  ) : !recaptchaLoaded ? (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <span>Loading reCAPTCHA...</span>
                    </>
                  ) : recaptchaToken ? (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Verified ✓</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Click to verify you're human</span>
                    </>
                  )}
                </button>
                {errors.recaptcha && (
                  <p className="mt-1 text-sm text-left text-red-400">{errors.recaptcha}</p>
                )}
                {!recaptchaLoaded && (
                  <p className="mt-1 text-sm text-left text-yellow-400">
                    Loading reCAPTCHA... Please wait.
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-semibold py-4 px-6 rounded-lg hover:from-cyan-500 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-cyan-400/50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {isSubmitting ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Sending Message...</span>
                  </div>
                ) : (
                  'Send Message'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;